import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Video, Film, Music, FileVideo, PartyPopper, Camera, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const videoProductionVideos = [
  {
    id: 'ymYWTdUBb5A',
    title: 'Datasignz Showreel',
    thumbnail: 'https://img.youtube.com/vi/ymYWTdUBb5A/hqdefault.jpg',
    embedUrl: 'https://www.youtube.com/embed/ymYWTdUBb5A'
  },
  {
    id: '7x1bCe_v3w0',
    title: 'BBC Maroons',
    thumbnail: 'https://img.youtube.com/vi/7x1bCe_v3w0/hqdefault.jpg',
    embedUrl: 'https://www.youtube.com/embed/7x1bCe_v3w0'
  },
  {
    id: 'kgfo8DBLzos',
    title: 'Unlock Nature',
    thumbnail: 'https://img.youtube.com/vi/kgfo8DBLzos/hqdefault.jpg',
    embedUrl: 'https://www.youtube.com/embed/kgfo8DBLzos'
  },
  {
    id: 'nJm2yKJhQJQ',
    title: 'Building Depot',
    thumbnail: 'https://img.youtube.com/vi/nJm2yKJhQJQ/hqdefault.jpg',
    embedUrl: 'https://www.youtube.com/embed/nJm2yKJhQJQ'
  },
  {
    id: 'CybiDk2eBt0',
    title: '94GreenTV Adventures',
    thumbnail: 'https://img.youtube.com/vi/CybiDk2eBt0/hqdefault.jpg',
    embedUrl: 'https://www.youtube.com/embed/CybiDk2eBt0'
  },
  {
    id: 'tLvGQT938T0',
    title: 'Unlock Nature',
    thumbnail: 'https://img.youtube.com/vi/tLvGQT938T0/hqdefault.jpg',
    embedUrl: 'https://www.youtube.com/embed/tLvGQT938T0'
  },
  {
    id: 'f0FV-F6imQQ',
    title: 'T-Idea',
    thumbnail: 'https://img.youtube.com/vi/f0FV-F6imQQ/hqdefault.jpg',
    embedUrl: 'https://www.youtube.com/embed/f0FV-F6imQQ'
  },
  {
    id: 'WbDG29AtV54',
    title: 'Brunch Brigade',
    thumbnail: 'https://img.youtube.com/vi/WbDG29AtV54/hqdefault.jpg',
    embedUrl: 'https://www.youtube.com/embed/WbDG29AtV54'
  },
  {
    id: 'LgthLPGgCAU',
    title: 'Building Depot',
    thumbnail: 'https://img.youtube.com/vi/LgthLPGgCAU/hqdefault.jpg',
    embedUrl: 'https://www.youtube.com/embed/LgthLPGgCAU'
  }
];

const VideoProductionPage = () => {
  const [currentVideo, setCurrentVideo] = useState(videoProductionVideos[0]);
  const [thumbnailStartIndex, setThumbnailStartIndex] = useState(0);
  const [videoKey, setVideoKey] = useState(0);
  const thumbnailsPerPage = 4;
  const { t } = useLanguage();

  const visibleThumbnails = videoProductionVideos.slice(
    thumbnailStartIndex,
    thumbnailStartIndex + thumbnailsPerPage
  );

  const canGoNext = thumbnailStartIndex + thumbnailsPerPage < videoProductionVideos.length;
  const canGoPrev = thumbnailStartIndex > 0;

  const nextThumbnails = () => {
    if (canGoNext) {
      setThumbnailStartIndex(thumbnailStartIndex + thumbnailsPerPage);
    }
  };

  const prevThumbnails = () => {
    if (canGoPrev) {
      setThumbnailStartIndex(thumbnailStartIndex - thumbnailsPerPage);
    }
  };

  const selectVideo = (video) => {
    setCurrentVideo(video);
    setVideoKey(prev => prev + 1);
  };

  const getEmbedUrl = () => {
    return `${currentVideo.embedUrl}?autoplay=1&mute=1&rel=0&enablejsapi=1&playsinline=1`;
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Page Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-montserrat-bold text-3xl md:text-4xl text-[#0b004c] mb-8">
              {t('videoProductionTitle')}
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto text-left mb-12">
              <p className="font-montserrat text-[#6a6a6a]">
                {t('videoProductionDesc1')}
              </p>
              <p className="font-montserrat text-[#6a6a6a]">
                {t('videoProductionDesc2')}
              </p>
            </div>

            {/* Video Player */}
            <div className="max-w-4xl mx-auto mb-8">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  key={videoKey}
                  className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
                  src={getEmbedUrl()}
                  title={currentVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>

            {/* Thumbnail Navigation */}
            <div className="max-w-4xl mx-auto mb-12">
              <div className="flex items-center gap-4">
                {/* Previous Arrow */}
                <button
                  onClick={prevThumbnails}
                  disabled={!canGoPrev}
                  className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                    canGoPrev
                      ? 'border-[#0b004c] text-[#0b004c] hover:bg-[#0b004c] hover:text-white cursor-pointer'
                      : 'border-gray-300 text-gray-300 cursor-not-allowed'
                  }`}
                  aria-label="Previous thumbnails"
                >
                  <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                </button>

                {/* Thumbnails */}
                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                  {visibleThumbnails.map((video) => (
                    <div key={video.id} className="flex flex-col">
                      <button
                        onClick={() => selectVideo(video)}
                        className={`relative aspect-video rounded-lg overflow-hidden group transition-all duration-300 ${
                          currentVideo.id === video.id
                            ? 'ring-2 ring-[#0b004c] ring-offset-2'
                            : 'hover:ring-2 hover:ring-[#6a6a6a] hover:ring-offset-2'
                        }`}
                      >
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = `https://img.youtube.com/vi/${video.id}/mqdefault.jpg`;
                          }}
                        />
                        {/* Play Overlay */}
                        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                          currentVideo.id === video.id
                            ? 'bg-[#0b004c]/50'
                            : 'bg-black/30 group-hover:bg-black/50'
                        }`}>
                          <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
                            currentVideo.id === video.id
                              ? 'bg-white text-[#0b004c]'
                              : 'bg-white/90 text-[#0b004c]'
                          }`}>
                            <Play className="w-5 h-5 md:w-6 md:h-6 ml-1" fill="currentColor" />
                          </div>
                        </div>
                      </button>
                      {/* Video Title */}
                      <p className={`mt-2 text-xs md:text-sm font-montserrat text-center truncate ${
                        currentVideo.id === video.id
                          ? 'text-[#0b004c] font-montserrat-bold'
                          : 'text-[#6a6a6a]'
                      }`}>
                        {video.title}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Next Arrow */}
                <button
                  onClick={nextThumbnails}
                  disabled={!canGoNext}
                  className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                    canGoNext
                      ? 'border-[#0b004c] text-[#0b004c] hover:bg-[#0b004c] hover:text-white cursor-pointer'
                      : 'border-gray-300 text-gray-300 cursor-not-allowed'
                  }`}
                  aria-label="Next thumbnails"
                >
                  <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
                </button>
              </div>

              {/* Page Indicator */}
              <div className="flex justify-center mt-4 gap-2">
                {Array.from({ length: Math.ceil(videoProductionVideos.length / thumbnailsPerPage) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setThumbnailStartIndex(index * thumbnailsPerPage)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      Math.floor(thumbnailStartIndex / thumbnailsPerPage) === index
                        ? 'bg-[#0b004c] w-6'
                        : 'bg-[#6a6a6a]/30 hover:bg-[#6a6a6a]/50'
                    }`}
                    aria-label={`Go to page ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { titleKey: 'tvCommercialsTitle', descKey: 'tvCommercialsDesc', Icon: Video, examples: ['TBL Cinemas', 'Heineken', 'Coca-Cola'] },
              { titleKey: 'corporateFilmsTitle', descKey: 'corporateFilmsDesc', Icon: Film, examples: ['Akira Resort', 'Building Depot'] },
              { titleKey: 'musicVideosTitle', descKey: 'musicVideosDesc', Icon: Music, examples: ['Local Artists', 'International Collaborations'] },
              { titleKey: 'documentariesTitle', descKey: 'documentariesDesc', Icon: FileVideo, examples: ['BBC', 'Birdland Suriname'] },
              { titleKey: 'promoVideosTitle', descKey: 'promoVideosDesc', Icon: PartyPopper, examples: ['Brand Launches', 'Product Reveals'] },
              { titleKey: 'afterMoviesTitle', descKey: 'afterMoviesDesc', Icon: Camera, examples: ['Conferences', 'Festivals', 'Corporate Events'] }
            ].map((service, index) => {
              const IconComponent = service.Icon;
              return (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300 group"
                >
                  <div className="w-12 h-12 bg-[#0b004c] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#0b004c]/80 transition-colors duration-300">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-montserrat-bold text-xl text-[#0b004c] mb-2">
                    {t(service.titleKey)}
                  </h3>
                  <p className="font-montserrat text-[#6a6a6a] text-sm">
                    {t(service.descKey)}
                  </p>
                  {service.examples && service.examples.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="font-montserrat text-xs text-[#6a6a6a] mb-2">{t('featuredClients')}</p>
                      <div className="flex flex-wrap gap-2">
                        {service.examples.map((example, i) => (
                          <span
                            key={i}
                            className="text-xs font-montserrat bg-[#0b004c]/10 text-[#0b004c] px-2 py-1 rounded"
                          >
                            {example}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0b004c] py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-montserrat-bold text-2xl md:text-3xl text-white mb-4">
            {t('readyBringVision')}
          </h2>
          <p className="font-montserrat text-white/80 mb-8 max-w-xl mx-auto">
            {t('videoCTADesc')}
          </p>
          <a
            href="/contact"
            className="inline-block bg-white text-[#0b004c] font-montserrat-bold px-8 py-3 rounded hover:bg-[#6a6a6a] hover:text-white transition-colors duration-300"
          >
            {t('getQuote')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VideoProductionPage;
